import { useEffect, useState } from 'react';
import VendorAutocomplete from '../VendorAutocomplete';
import {
  PRODUCT_CATEGORIES,
  findDuplicate,
  productImageUrl,
  unitMargin,
  uploadProductImage,
} from '../../lib/products';
import { formatJMD } from '../../lib/finances';
import { toHttpUrl } from '../../lib/url';

const BLANK = {
  name: '',
  category: '',
  supplier: '',
  productInfo: '',
  cost: '',
  price: '',
  stock: '0',
  lowStockAt: '3',
  productUrl: '',
};

// Add / edit one product. Doubles as both, like EntryForm in Accounting:
// pass `initialProduct` to pre-fill and switch it into update mode.
//
// The supplier field is a real vendor — the autocomplete's inline "add as new
// vendor" creates it on the spot, so typing "Skin1004" here puts Skin1004 in
// the Vendors list rather than in a second, parallel list of supplier names.
export default function ProductForm({
  initialProduct,
  products,
  vendors,
  onCreateVendor,
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // The photo is three separate things: the file just chosen (not uploaded
  // yet), what to show right now, and whether the saved image is being
  // cleared. Collapsing them loses the difference between "unchanged" and
  // "deliberately removed".
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoCleared, setPhotoCleared] = useState(false);

  const pickPhoto = (file) => {
    setPhotoFile(file);
    setPhotoCleared(!file);
    // Object URLs are revoked when replaced or on unmount, below — a preview
    // per keystroke-worth of file picking otherwise leaks them.
    setPhotoPreview((previous) => {
      if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  useEffect(
    () => () => {
      if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview]
  );

  useEffect(() => {
    if (initialProduct) {
      setForm({
        name: initialProduct.name || '',
        category: initialProduct.category || '',
        supplier: initialProduct.vendor?.name || '',
        productInfo: initialProduct.product_info || '',
        cost: initialProduct.cost_jmd != null ? String(initialProduct.cost_jmd) : '',
        price: initialProduct.price_jmd != null ? String(initialProduct.price_jmd) : '',
        stock: String(initialProduct.stock_qty ?? 0),
        lowStockAt:
          initialProduct.low_stock_at != null ? String(initialProduct.low_stock_at) : '',
        productUrl: initialProduct.product_url || '',
      });
      setPhotoPreview(productImageUrl(initialProduct.image_path));
    } else {
      setForm(BLANK);
      setPhotoPreview(null);
    }
    setPhotoFile(null);
    setPhotoCleared(false);
    setError(null);
    // Re-running when photoPreview changes would immediately undo the user's
    // own pick; this effect exists only to load the product being edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProduct]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // A warning, not a block. Two suppliers can sell a product of the same
  // name, so refusing the save would be wrong — but silently ending up with
  // the same product twice, each holding half the stock count, is the actual
  // failure mode this catalogue has.
  const duplicate = findDuplicate(products, form.name, initialProduct?.id);

  // Derived from what's typed, not from the saved row, so it updates as the
  // price is being decided.
  const margin = unitMargin({ price_jmd: form.price, cost_jmd: form.cost });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError('Give the product a name.');
      return;
    }

    // The supplier is stored as a vendor id, so a name typed but never picked
    // from the list has to be resolved — matched to an existing vendor, or
    // created. Otherwise the supplier silently disappears on save.
    const typed = form.supplier.trim();
    let vendorId = null;
    if (typed) {
      const match = vendors.find((v) => v.name.toLowerCase() === typed.toLowerCase());
      vendorId = match ? match.id : null;
      if (!match) {
        try {
          const created = await onCreateVendor(typed);
          vendorId = created.id;
        } catch (vendorError) {
          setError(`Couldn't save "${typed}" as a supplier: ${vendorError.message}`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      // Uploaded before the row is written, so a failed upload doesn't leave
      // a product pointing at an image that isn't there.
      let imagePath = initialProduct?.image_path || null;
      if (photoFile) {
        try {
          imagePath = await uploadProductImage(photoFile);
        } catch (uploadError) {
          setError(`Couldn't upload that photo: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      } else if (photoCleared) {
        imagePath = null;
      }

      await onSubmit({
        image_path: imagePath,
        name: form.name.trim(),
        category: form.category || null,
        vendor_id: vendorId,
        product_info: form.productInfo.trim() || null,
        cost_jmd: form.cost === '' ? null : Number(form.cost),
        price_jmd: form.price === '' ? null : Number(form.price),
        stock_qty: Math.max(0, Math.round(Number(form.stock) || 0)),
        low_stock_at: form.lowStockAt === '' ? null : Math.max(0, Number(form.lowStockAt)),
        // Normalised on the way in so the stored value is always a real
        // http(s) URL — and so it matches the link-preview cache key.
        product_url: toHttpUrl(form.productUrl),
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <h2 className="product-form-title">
        {initialProduct ? `Edit ${initialProduct.name}` : 'Add a product'}
      </h2>

      {/* Twelve columns so fields can be sized to what they hold: a product
          name needs room, a category picker doesn't. An even auto-fit grid
          gave a two-digit stock count the same width as the name. */}
      <div className="product-form-grid">
        <label className="col-7">
          Product name
          <input type="text" value={form.name} onChange={set('name')} autoFocus />
          {duplicate && (
            <span className="product-form-warning">
              &ldquo;{duplicate.name}&rdquo; is already in the catalogue — edit that one instead of
              adding a second, or carry on if this really is a different product.
            </span>
          )}
        </label>

        <label className="col-5">
          Category
          <select value={form.category} onChange={set('category')}>
            <option value="">Choose a category…</option>
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="col-12">
          Supplier
          <VendorAutocomplete
            value={form.supplier}
            onChange={(supplier) => setForm((f) => ({ ...f, supplier }))}
            vendors={vendors}
            onCreateVendor={onCreateVendor}
            placeholder="Start typing — new suppliers are added for you"
          />
        </label>

        {/* Money and stock as two matched groups rather than four loose
            fields. Each is a flex row, not a grid: a hint sharing a grid row
            with fixed-width number fields overlapped them as soon as the text
            was longer than its column. */}
        <div className="product-form-group col-6">
          <span className="product-form-group-title">Pricing</span>
          <div className="product-form-group-fields">
            <label>
              Cost each
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="1400"
                value={form.cost}
                onChange={set('cost')}
              />
            </label>
            <label>
              Sells for
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="2500"
                value={form.price}
                onChange={set('price')}
              />
            </label>
          </div>
          {/* Live, because margin is the thing you're actually deciding when
              you type a price — reading it back after saving is too late. */}
          <p className={'product-form-hint' + (margin ? ' product-form-margin' : '')}>
            {margin
              ? `${formatJMD(margin.profit)} profit each${
                  margin.percent === null ? '' : ` · ${margin.percent.toFixed(0)}% margin`
                }`
              : 'Both figures in JMD. Fill in the cost to see the margin.'}
          </p>
        </div>

        <div className="product-form-group col-6">
          <span className="product-form-group-title">Stock</span>
          <div className="product-form-group-fields">
            <label>
              In stock now
              <input type="number" min="0" step="1" value={form.stock} onChange={set('stock')} />
            </label>
            <label>
              Flag as low at
              <input
                type="number"
                min="0"
                step="1"
                placeholder="3"
                value={form.lowStockAt}
                onChange={set('lowStockAt')}
              />
            </label>
          </div>
          <p className="product-form-hint">
            Flagged for restock at or below this count. Leave blank to only be told at zero.
          </p>
        </div>

        <label className="col-12">
          Product page on the website
          {/* Free text, not type="url": a link gets pasted or typed as
              "skinfairyja.com/shop/x" as often as with the scheme, and the
              browser's url validation would reject that outright. */}
          <input
            type="text"
            placeholder="skinfairyja.com/products/…"
            value={form.productUrl}
            onChange={set('productUrl')}
          />
          <span className="product-form-hint">
            Used for the thumbnail beside the product, and to open the live page in one click.
          </span>
        </label>

        {/* The native file input is the most conspicuously unstyled control
            on any page, so it's visually hidden and driven by its label —
            same treatment as the marketing uploader. */}
        <div className="product-form-photo col-12">
          <span className="product-form-group-title">Photo</span>
          <div className="product-form-photo-row">
            <div className="product-form-photo-preview">
              {photoPreview ? (
                <img src={photoPreview} alt="" />
              ) : (
                <span aria-hidden="true">🧴</span>
              )}
            </div>
            <div className="product-form-photo-actions">
              <label className="ghost-button product-form-photo-button">
                {photoPreview ? 'Replace photo' : 'Upload a photo'}
                <input
                  type="file"
                  accept="image/*"
                  className="product-form-file"
                  onChange={(e) => pickPhoto(e.target.files[0] || null)}
                />
              </label>
              {photoPreview && (
                <button type="button" className="link-button" onClick={() => pickPhoto(null)}>
                  Remove
                </button>
              )}
              <span className="product-form-hint">
                Your own product photo. Without one, the picture comes from the website link above
                — or nothing, if that page doesn&rsquo;t publish an image.
              </span>
            </div>
          </div>
        </div>

        <label className="col-12">
          Product information
          <textarea
            rows={4}
            placeholder="What it does, who it suits, how to use it…"
            value={form.productInfo}
            onChange={set('productInfo')}
          />
        </label>
      </div>

      {error && <p className="product-form-error">{error}</p>}

      <div className="product-form-actions">
        <button type="submit" className="primary-action" disabled={submitting}>
          {submitting ? 'Saving…' : initialProduct ? 'Update product' : 'Add product'}
        </button>
        <button type="button" className="ghost-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
